@use "sass:string";
@use "breakpoint.module" as *;
@use "utils.module" as *;

/*
This file creates a few things for each type option design comes up with:
    * a css variable for those values that can be grouped into the css `font` shorthand property
        * this will incorporate the appropriate values at different breakpoints as design decrees
        * by default it tracks the desktop variant (with -lg suffix), swapping at smaller breakpoints if variations (-md or -sm) variables exist
    * a utilty class with that font property specified along with any other (line height? text-transform? letter-spacing?) variables that don't fit
        * this can be applied directly to elements or (preferably) composed

We have not come up with any non-painful process for getting the component variables defined in a way that matches figma. Options we have not rejected include:
    * pick some figma plugin, rework all this to do things the way it wants to do things
    * integrate Claude with Figma's local mcp server and ask it to do it
*/

$heading-a-type-styles: 200, 120, 88;
$heading-b-type-styles:
    "144", "120", "104", "80", "64", "56", "48", "40", "32", "24";
$subhead-type-styles: "64", "48", "40", "32", "24";
$paragraph-type-styles: "24", "20", "16", "14", "12";
$shorttext-type-styles: "24", "20", "16", "14", "12";
$button-type-styles: "20", "16", "12";

$type-style-variations: (
    "ha": $heading-a-type-styles,
    "hb": $heading-b-type-styles,
    "sh": $subhead-type-styles,
    "p": $paragraph-type-styles,
    "st": $shorttext-type-styles,
    "button": $button-type-styles,
);

@each $type in $heading-a-type-styles {
    .fontHA#{$type} {
        font: var(--font-ha-#{$type});
        letter-spacing: -0.01em;
    }

    .fontHAI#{$type} {
        composes: fontHA#{$type};
        font-style: italic;
    }
}

@each $type in $heading-b-type-styles {
    .fontHB#{$type} {
        font: var(--font-hb-#{$type});
        font-feature-settings: "ss02" on;
    }
}

@each $type in $subhead-type-styles {
    .fontSH#{$type} {
        font: var(--font-sh-#{$type});
        letter-spacing: -0.01em;
    }
}

@each $type in $paragraph-type-styles {
    .fontP#{$type} {
        font: var(--font-p-#{$type});
    }
}

@each $type in $shorttext-type-styles {
    .fontST#{$type} {
        font: var(--font-st-#{$type});
    }
}

@each $type in $button-type-styles {
    .fontB#{$type} {
        font: var(--font-button-#{$type});
    }
}

@font-face {
    font-family: "PP Right Grotesk";
    src: url("/static/fonts/pp-right-grotesk-narrow-medium/PPRightGrotesk-NarrowMedium.otf")
        format("opentype");
    font-style: normal;
    font-weight: 500;
}

@font-face {
    font-family: "PP Right Grotesk Tight";
    src: url("/static/fonts/pp-right-grotesk-tight-medium/PPRightGrotesk-TightMedium.otf")
        format("opentype");
    font-style: normal;
    font-weight: 500;
}

// Think this is the first time we're using variable fonts. Potential future improvement - additional @font-face declarations
// to use static variations of the file when variable font @supports isn't available
@font-face {
    font-family: Newsreader;
    src: url("/static/fonts/newsreader/Newsreader-VariableFont_opsz\,wght.ttf")
        format("truetype");
    font-style: normal;
    font-weight: 300;
}

@font-face {
    font-family: Newsreader;
    src: url("/static/fonts/newsreader/Newsreader-Italic-VariableFont_opsz\,wght.ttf")
        format("truetype");
    font-style: italic;
    font-weight: 300;
}

@font-face {
    font-family: Avenir;
    src: url("/static/fonts/avenir-next/Linotype\ -\ Avenir\ Next\ World\ Regular\ copy.otf")
        format("opentype");
    font-style: normal;
    font-weight: 400;
}

@font-face {
    font-family: Avenir;
    src: url("/static/fonts/avenir-next/Linotype\ -\ Avenir\ Next\ World\ Demi\ copy.otf")
        format("opentype");
    font-style: normal;
    font-weight: 800;
}

:root {
    @each $prefix, $variants in $type-style-variations {
        // default size is the "large" size
        @each $type in $variants {
            --font-#{$prefix}-#{$type}: var(--font-#{$prefix}-#{$type}-lg);
        }

        // At tablet breakpoint, switch to "medium" size (use "large" if no medium is specified)
        @media ($below-tablet) {
            @each $type in $variants {
                --font-#{$prefix}-#{$type}: var(
                    --font-#{$prefix}-#{$type}-md,
                    var(--font-#{$prefix}-#{$type}-lg)
                );
            }
        }

        // At mobile breakpoint, switch to "small" size (use "large" if no small is specified
        @media ($below-mobile) {
            @each $type in $variants {
                --font-#{$prefix}-#{$type}: var(
                    --font-#{$prefix}-#{$type}-sm,
                    var(--font-#{$prefix}-#{$type}-lg)
                );
            }
        }
    }

    // Fonts
    // Header A
    --font-ha-200-lg: 300 144px / 1 Newsreader;
    --font-ha-200-md: 300 120px / 1 Newsreader;
    --font-ha-200-sm: 300 64px / 1 Newsreader;
    --font-ha-120-lg: 300 88px / 1 Newsreader;
    --font-ha-120-md: 300 72px / 1 Newsreader;
    --font-ha-120-sm: 300 48px / 1 Newsreader;
    --font-ha-88-lg: 300 64px / 1 Newsreader;
    --font-ha-88-md: 300 48px / 1 Newsreader;
    --font-ha-88-sm: 300 32px / 1 Newsreader;

    // Header B
    --font-hb-144-lg: 500 120px / 0.9 "PP Right Grotesk Tight";
    --font-hb-144-md: 500 104px / 0.9 "PP Right Grotesk Tight";
    --font-hb-144-sm: 500 56px / 0.9 "PP Right Grotesk Tight";
    --font-hb-120-lg: 500 104px / 0.9 "PP Right Grotesk Tight";
    --font-hb-120-md: 500 72px / 0.9 "PP Right Grotesk Tight";
    --font-hb-120-sm: 500 48px / 0.9 "PP Right Grotesk Tight";
    --font-hb-104-lg: 500 80px / 0.9 "PP Right Grotesk Tight";
    --font-hb-104-md: 500 64px / 0.9 "PP Right Grotesk Tight";
    --font-hb-104-sm: 500 40px / 0.9 "PP Right Grotesk Tight";
    --font-hb-80-lg: 500 64px / 0.9 "PP Right Grotesk Tight";
    --font-hb-80-md: 500 56px / 0.9 "PP Right Grotesk Tight";
    --font-hb-80-sm: 500 32px / 0.9 "PP Right Grotesk";
    --font-hb-64-lg: 500 56px / 0.9 "PP Right Grotesk Tight";
    --font-hb-64-md: 500 48px / 0.9 "PP Right Grotesk Tight";
    --font-hb-64-sm: 500 32px / 0.9 "PP Right Grotesk";
    --font-hb-56-lg: 500 48px / 0.9 "PP Right Grotesk Tight";
    --font-hb-56-md: 500 40px / 0.9 "PP Right Grotesk Tight";
    --font-hb-56-sm: 500 28px / 0.9 "PP Right Grotesk";
    --font-hb-48-lg: 500 40px / 0.9 "PP Right Grotesk Tight";
    --font-hb-48-md: 500 32px / 0.9 "PP Right Grotesk";
    --font-hb-48-sm: 500 28px / 0.9 "PP Right Grotesk";
    --font-hb-40-lg: 500 32px / 0.9 "PP Right Grotesk";
    --font-hb-40-md: 500 28px / 0.9 "PP Right Grotesk";
    --font-hb-40-sm: 500 24px / 0.9 "PP Right Grotesk";
    --font-hb-32-lg: 500 28px / 0.9 "PP Right Grotesk";
    --font-hb-32-md: 500 24px / 0.9 "PP Right Grotesk";
    --font-hb-32-sm: 500 22px / 0.9 "PP Right Grotesk";
    --font-hb-24-lg: 500 24px / 0.9 "PP Right Grotesk";
    --font-hb-24-md: 500 22px / 0.9 "PP Right Grotesk";
    --font-hb-24-sm: 500 20px / 0.9 "PP Right Grotesk";

    // Sub head
    --font-sh-64-lg: 300 48px / 1.2 Newsreader;
    --font-sh-64-md: 300 40px / 1.2 Newsreader;
    --font-sh-64-sm: 300 32px / 1.2 Newsreader;
    --font-sh-48-lg: 300 40px / 1.2 Newsreader;
    --font-sh-48-md: 300 32px / 1.2 Newsreader;
    --font-sh-48-sm: 300 24px / 1.2 Newsreader;
    --font-sh-40-lg: 300 32px / 1.2 Newsreader;
    --font-sh-40-md: 300 28px / 1.2 Newsreader;
    --font-sh-40-sm: 300 24px / 1.1 Newsreader;
    --font-sh-32-lg: 300 24px / 1.1 Newsreader;
    --font-sh-32-md: 300 22px / 1.1 Newsreader;
    --font-sh-32-sm: 300 20px / 1 Newsreader;
    --font-sh-24-lg: 300 22px / 1.1 Newsreader;
    --font-sh-24-md: 300 20px / 1.1 Newsreader;
    --font-sh-24-sm: 300 18px / 1 Newsreader;

    // Paragraph
    --font-p-24-lg: 400 18px / 1.6 Avenir;
    --font-p-24-sm: 400 16px / 1.6 Avenir;
    --font-p-20-lg: 400 16px / 1.6 Avenir;
    --font-p-20-sm: 400 14px / 1.6 Avenir;
    --font-p-16-lg: 400 14px / 1.3 Avenir;
    --font-p-16-sm: 400 12px / 1.3 Avenir;
    --font-p-14-lg: 400 12px / 1.3 Avenir;
    --font-p-14-sm: 400 11px / 1.3 Avenir;
    --font-p-12-lg: 400 12px / 1.3 Avenir;
    --font-p-12-sm: 400 11px / 1.3 Avenir;

    // Short text
    --font-st-24-lg: 400 18px / 1.2 Avenir;
    --font-st-24-sm: 400 16px / 1.2 Avenir;
    --font-st-20-lg: 400 16px / 1.2 Avenir;
    --font-st-20-sm: 400 14px / 1.2 Avenir;
    --font-st-16-lg: 400 14px / 1.2 Avenir;
    --font-st-16-sm: 400 12px / 1.2 Avenir;
    --font-st-14-lg: 400 12px / 1.2 Avenir;
    --font-st-14-sm: 400 11px / 1.2 Avenir;
    --font-st-12-lg: 400 12px / 1.2 Avenir;
    --font-st-12-sm: 400 11px / 1.2 Avenir;

    // Button
    --font-button-20-lg: 800 16px / 1.2 Avenir;
    --font-button-20-sm: 800 14px / 1.2 Avenir;
    --font-button-16-lg: 800 14px / 1.2 Avenir;
    --font-button-16-sm: 800 12px / 1.2 Avenir;
    --font-button-12-lg: 800 12px / 1.2 Avenir;
}
